import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ConstantsService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { WorkingModel } from '../enums/working-model.enum';
import { Facility } from '../interfaces/facility';
import { JobDetail } from '../interfaces/job-detail';

@Injectable({
  providedIn: 'root',
})
export class SEOService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly constantsService = inject(ConstantsService);
  private readonly translateService = inject(TranslocoService);

  website: string;

  private divider = ' | ';

  updateTitle(title: string) {
    this.title.setTitle(title);
    this.meta.updateTag({ property: 'og:title', content: title });
  }

  updateDescription(desc: string) {
    this.meta.updateTag({ property: 'description', content: desc });
    this.meta.updateTag({
      property: 'og:description',
      content:
        desc.indexOf(this.divider) !== -1 ? desc.substring(desc.indexOf(this.divider) + this.divider.length) : desc,
    }); //remove job name, as already present in title
  }

  updateKeywords(keywords: string[]) {
    let keywordString: string = '';
    keywords.forEach((keyword, index) => (keywordString += `${keyword}${index < keywords.length - 1 ? ', ' : ''}`));
    this.meta.updateTag({ property: 'keywords', content: keywordString });
  }

  updateImage(img: string) {
    this.meta.updateTag({ property: 'og:image', content: img });
  }

  setWebsite(url?: string) {
    if (url) {
      this.website = url;
    } else {
      this.website = 'https://www.catch-talents.de/';
    }
  }

  /**
   * Updates document title and job meta description after translations for the active language are loaded.
   * Re-emits when the language changes (Transloco selectTranslate).
   */
  updateJobPageSeo(job: JobDetail): Observable<void> {
    return this.translateService
      .selectTranslate('jobboard.views.job.title', {
        jobTitle: job.jobTitle,
        companyName: job.companyTitle,
      })
      .pipe(
        tap((pageTitle) => {
          this.updateTitle(pageTitle);
          this.updateDescription(this.buildJobMetaDescription(job));
        })
      );
  }

  /**
   * Initiative / unsolicited application route title — waits for i18n like {@link updateJobPageSeo}.
   */
  updateUnsolicitedApplyTitle(companyName: string): Observable<void> {
    return this.translateService.selectTranslate('jobboard.views.apply.unsolicited-title', { companyName }).pipe(
      tap((pageTitle) => this.updateTitle(pageTitle))
    );
  }

  private buildJobMetaDescription(job: JobDetail): string {
    let description: string = this.translateService.translate('jobboard.views.job.title', {
      jobTitle: job.jobTitle,
      companyName: job.companyTitle,
    });
    description += this.divider;
    description += this.translateService.translate(
      this.constantsService.getLabelByValue(this.constantsService.employmentTypeConstants, job.employmentType)
    );
    description += this.divider;
    description += this.translateService.translate(
      this.constantsService.getLabelByValue(this.constantsService.workingHoursConstants, job.workingTime)
    );
    description += this.divider;
    if ((job.workingModel === WorkingModel.ON_SITE || job.workingModel === WorkingModel.HYBRID) && job.workLocations.length > 0) {
      if (job.workingModel === WorkingModel.HYBRID) {
        description += this.translateService.translate('jobboard.views.jobboard.list.hybrid') + ', ';
      }
      const filteredLocations: Facility[] = job.workLocations.filter(
        (thing, index, self) => index === self.findIndex(t => t.city === thing.city)
      );
      filteredLocations.forEach((location, index) => {
        description += `${location.city}${index < filteredLocations.length - 1 ? ', ' : ''}`;
      });
    } else if (job.workingModel === WorkingModel.FIELD_SERVICE) {
      description += this.translateService.translate('jobboard.views.jobboard.list.field-service');
    } else {
      description += this.translateService.translate('jobboard.views.jobboard.list.remote');
    }
    return description;
  }
}
